import React from "react";
import { AlertOctagon, Mail, LogOut } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Module 6D Phase 4 — Full-page "Studio Suspended" block.
 *
 * Rendered by AuthProvider when:
 *   • `/auth/me` returns `studio.plan_status === "suspended"`, OR
 *   • Any API call returns 403 `{code: "studio_suspended", ...}` (the
 *     interceptor in `lib/api.js` dispatches `lead_studio:suspended`
 *     with the payload; AuthProvider catches it and flips state).
 *
 * Per the user's hard requirement: the screen MUST show the REASON
 * and a clear APPEAL path. Suspend blocks ACTION; it does NOT hide
 * WHY the owner was suspended (that would be hostile).
 */
export default function SuspendedScreen({
  studioName,
  reason,
  suspendedAt,
  supportEmail,
}) {
  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try { localStorage.removeItem("ls.auth.token"); } catch { /* ignore */ }
    window.location.replace("/");
  };

  const subject = encodeURIComponent(
    `Appeal: ${studioName || "studio"} suspension`,
  );
  const body = encodeURIComponent(
    `Hi support team,\n\nMy studio was suspended on ${suspendedAt || "(date unknown)"}.\n\n` +
    `Reason given:\n${reason || "(no reason supplied)"}\n\n` +
    `I'd like to appeal because:\n\n[describe context here]\n\nThanks.`,
  );
  const mailto = supportEmail
    ? `mailto:${supportEmail}?subject=${subject}&body=${body}`
    : null;

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6 py-10"
      style={{ backgroundColor: "var(--brand-paper)" }}
      data-testid="suspended-screen"
    >
      <div
        className="max-w-xl w-full"
        style={{
          backgroundColor: "#fff",
          border: "1px solid var(--brand-border)",
          padding: "40px 32px",
          borderRadius: 0,
        }}
      >
        <div
          className="flex items-center justify-center mb-5"
          style={{
            width: 56, height: 56, margin: "0 auto 24px",
            backgroundColor: "#7c2d12", color: "#fbbf24",
            borderRadius: "50%",
          }}
        >
          <AlertOctagon size={26} />
        </div>
        <div
          className="kicker text-center mb-3"
          style={{ color: "#7c2d12" }}
          data-testid="suspended-screen-kicker"
        >
          STUDIO SUSPENDED
        </div>
        <h1
          className="font-display text-3xl text-center mb-4"
          style={{ color: "var(--brand-ink)" }}
          data-testid="suspended-screen-title"
        >
          {studioName || "Your studio"} is currently suspended
        </h1>
        <p
          className="text-sm text-center mb-6"
          style={{ color: "var(--brand-ink-muted, #54534b)" }}
        >
          Access to your studio is paused. Your data is preserved — nothing
          has been deleted. You can appeal below.
        </p>

        <div
          className="mb-6 px-4 py-3"
          style={{
            backgroundColor: "var(--brand-paper)",
            border: "1px solid var(--brand-border)",
          }}
        >
          <div className="kicker mb-2" style={{ fontSize: 11 }}>REASON</div>
          <div
            className="text-sm leading-relaxed"
            style={{ color: "var(--brand-ink)" }}
            data-testid="suspended-screen-reason"
          >
            {reason || "No reason supplied. Please contact support for details."}
          </div>
          {suspendedAt ? (
            <div
              className="mt-3 text-xs opacity-70"
              style={{ color: "var(--brand-ink-muted, #54534b)" }}
              data-testid="suspended-screen-date"
            >
              Suspended on {new Date(suspendedAt).toLocaleString()}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2.5">
          {mailto ? (
            <a
              href={mailto}
              data-testid="suspended-screen-appeal-link"
              className="inline-flex items-center justify-center gap-2 text-sm font-semibold tracking-wide uppercase"
              style={{
                padding: "12px 18px",
                backgroundColor: "var(--brand-primary)",
                color: "#fff",
                border: "1px solid var(--brand-primary)",
                borderRadius: 0,
                textDecoration: "none",
              }}
            >
              <Mail size={15} />
              Appeal &amp; contact support
            </a>
          ) : null}
          <button
            type="button"
            onClick={handleLogout}
            data-testid="suspended-screen-logout-button"
            className="inline-flex items-center justify-center gap-2 text-sm tracking-wide uppercase"
            style={{
              padding: "10px 18px",
              backgroundColor: "transparent",
              color: "var(--brand-ink)",
              border: "1px solid var(--brand-border)",
              borderRadius: 0,
              cursor: "pointer",
            }}
          >
            <LogOut size={14} />
            Log out
          </button>
        </div>

        {supportEmail ? (
          <div
            className="mt-6 pt-4 text-center text-xs opacity-60"
            style={{
              borderTop: "1px solid var(--brand-border)",
              color: "var(--brand-ink)",
            }}
            data-testid="suspended-screen-support-email"
          >
            Or email us directly at <strong>{supportEmail}</strong>
          </div>
        ) : null}
      </div>
    </div>
  );
}
